import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Form, Input, DatePicker, Select } from "antd";
import dayjs from "dayjs";
import CrudButtons from "components/button/crudbuttons";
import FormItem from "components/fields/FormItem";
import { requiredField } from "utils/crudSagaHelper";
import { MODULE_EMPLOYEES } from "variables/constants";
import { 
	getEmployeeAssignedAssetDetails,
	doEmployeeAssignedAssetCreate,
	doEmployeeAssignedAssetUpdate,
	getAssetOptions,
} from "../../../../../redux/employeeassignedasset/employeeAssignedAssetState"

const AssignedAssetsForm = ({
	recordId,
	mode,
	onCancel,
	submitLabel,
}) => {
	const [form] = Form.useForm();
  	const DATE_FORMAT = "YYYY-MM-DD";
	const { employeeId } = useParams();
	const dispatch = useDispatch();
	const recordData = useSelector((state) => state.employeeAssignedAsset.employeeAssignedAssetDetails?.data);
	const assetOptions = useSelector((state) => state.employeeAssignedAsset.assetOptions.items);
	
	useEffect(() => {
		dispatch(getEmployeeAssignedAssetDetails(recordId));
	}, [dispatch, recordId]);

	const handleAssetSearch = (value) => {
		if (!value || value.length < 2) return;

		dispatch(
			getAssetOptions({
				keyword: value,
			})
		);
	};

	useEffect(() => {
		form.resetFields();

		if (!recordData) return;

		form.setFieldsValue({
			...recordData,
			eaa_asset_id: recordData?.eaa_asset_id
			? {
				value: recordData.eaa_asset_id,
				label:
					recordData.asset_name ||
					recordData.asset?.equipment_type_name,
				}
			: null,
			eaa_employee_id: employeeId,
			eaa_date_provided: recordData?.eaa_date_provided
				? dayjs(recordData.eaa_date_provided)
				: null,
			eaa_date_returned: recordData?.eaa_date_returned
				? dayjs(recordData.eaa_date_returned)
				: null,
		});
	}, [recordData, employeeId, form]);

	const onSubmit = (formData) => {
		formData.eaa_employee_id = employeeId;

		formData.eaa_asset_id =
			formData.eaa_asset_id?.value ?? formData.eaa_asset_id;

		formData.eaa_date_provided =
			formData.eaa_date_provided?.format(DATE_FORMAT) || null;

		formData.eaa_date_returned =
			formData.eaa_date_returned?.format(DATE_FORMAT) || null;

		if (recordData?.eaa_id > 0) {
			dispatch(
				doEmployeeAssignedAssetUpdate({
					...formData,
					eaa_id: recordData.eaa_id,
				})
			);
		} else {
			dispatch(doEmployeeAssignedAssetCreate(formData));
		}
	};

	return (
		<Form
			layout="vertical"
			className={mode !== "view" ? "form" : ""}
			form={form}
			onFinish={onSubmit}
		>
			<FormItem
				name="eaa_id"
				hidden
			>
				<Input type="hidden" />
			</FormItem>

			<FormItem
				name="eaa_employee_id"
				hidden
			> 
				<Input type="hidden" />
			</FormItem>

			{mode !== "view" ? (
				<FormItem
					label="Asset"
					name="eaa_asset_id"
					rules={[requiredField()]}
					className="col-span-2"
				>
					<Select
						id="eaa_asset_id"
						className="input-field"
						showSearch
						allowClear
						disabled={recordData?.eaa_id > 0}
						onSearch={handleAssetSearch}
						options={assetOptions}
						placeholder="Search Asset by Type, Model, Brand or Tag"
						optionRender={(option) => (
							<div className="flex justify-between items-start w-full">
								<span>{option.data.label}</span>
								{option.data.hyvid_tag && (
									<span className="text-gray-500">
										TAG: {option.data.hyvid_tag}
									</span>
								)}
							</div>
						)}
						filterOption={false}
						onChange={(value, option) => {
							form.setFieldsValue({
								eaa_condition_issued_id: option?.condition_id ?? null,
							});
						}}
					/>
				</FormItem>
			) : (
				<div className="p-2">
					<h2 className="text-sm font-medium muted-text">Asset</h2>
					<label className="text-base label-value dark:text-white">
						{recordData?.asset_name} {recordData?.asset_type}
					</label>
				</div>
			)}

			<div className="grid grid-cols-3 gap-4">
				{mode !== "view" ? (

					<FormItem
						label="Condition"
						name="eaa_condition_issued_id"
						rules={[requiredField()]}
						className="w-full"
					>
						<Select
							id="eaa_condition_issued_id"
							className="input-field"
							showSearch
							allowClear
							options={recordData?.conditionOptions || []}
						/>
					</FormItem>
				) : (
					<div className="p-2">
						<h2 className="text-sm font-medium muted-text">Condition</h2>
					<label className="text-base label-value dark:text-white">
						{recordData?.asset_condition_name}
					</label>
					</div>
				)}
				{mode !== "view" ? (
					<FormItem
						label="Date Assigned"
						name="eaa_date_provided"
						rules={[requiredField()]}
						className="w-full"
					>
						<DatePicker
							id="eaa_date_provided"
							className="input-field"
							format={DATE_FORMAT}
							style={{ width: "100%" }}
						/>
					</FormItem>
				) : (
					<div className="p-2">
						<h2 className="text-sm font-medium muted-text">Date Assigned</h2>
						<label className="text-base label-value dark:text-white">
							{recordData?.eaa_date_provided}
						</label>
					</div>
				)}
				{mode !== "view" ? (
					<FormItem
						label="Date Returned"
						name="eaa_date_returned"
						className="w-full"
					>
							<DatePicker
								id="eaa_date_returned"
								className="input-field"
								format={DATE_FORMAT}
								style={{ width: "100%" }}
							/>
					</FormItem>
				) : (
					<div className="p-2">
						<h2 className="text-sm font-medium muted-text">Date Returned</h2>
						<label className="text-base label-value dark:text-white">
							{recordData?.eaa_date_returned || "-"}
						</label>
					</div>
				)}
			</div>

			<div className="w-full flex justify-end">
				{mode === "edit" && (
					<CrudButtons
						mode={mode}
						recordId={recordData?.eaa_id ?? 0}
						isModal={true}
						modalId={recordData?.eaa_id > 0 ? 'edit_assigned_asset' : 'add_assigned_asset'}
						cancelLabel="Cancel"
						doHideModal={onCancel}
						submitLabel={submitLabel}
						moduleId={MODULE_EMPLOYEES}
					/>
				)}
			</div>
		</Form>
	);
};

export default (AssignedAssetsForm);